import React from 'react';
import { useTranslation } from 'react-i18next';
import Flex from '@wix/da-ds/pkg/Flex';
import FlexItem from '@wix/da-ds/pkg/Flex/Item';
import { PapiDeviation } from '@wix/da-papi-types';
import ExclusiveButton from '@wix/da-ds/pkg/Buttons/ExclusiveButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import GiftAnimated from '@wix/da-ds/pkg/Icons/GiftAnimated';
import useAdoptableState from '../../../AdoptableInfo/useAdoptableState';
import { useEndSaleAction, useViewOffersAction, useSellerGiveAsGift } from '../../hooks';

interface Props {
  deviation: PapiDeviation;
  adoptableState: ReturnType<typeof useAdoptableState>;
}

const SellerButtons: React.FC<Props> = ({
  deviation,
  adoptableState: { canSellerViewOffers, isFreeGiftable },
}) => {
  const { t } = useTranslation();

  const sectionname = 'side-rail';
  const [onViewOffers, viewOffersBiData] = useViewOffersAction(deviation, {
    sectionname,
  });
  const [onGiveAsGift, giftingBiData] = useSellerGiveAsGift(deviation, {
    sectionname,
  });
  const [onEndSale, endSaleBiData] = useEndSaleAction(deviation, {
    sectionname,
  });

  return (
    <React.Fragment>
      <ExclusiveButton
        key="end-sale"
        priority="secondary"
        onClick={onEndSale}
        biData={endSaleBiData}
      >
        {t('sidebar.exclusives.buttons.seller.endSale')}
      </ExclusiveButton>
      {
        <Flex gap={16} fullWidth>
          {canSellerViewOffers && (
            <FlexItem grow>
              <ExclusiveButton
                key="view-offers"
                priority="primary"
                onClick={onViewOffers}
                biData={viewOffersBiData}
                variant="compact"
              >
                {t('sidebar.exclusives.buttons.seller.viewOffers')}
              </ExclusiveButton>
            </FlexItem>
          )}
          {isFreeGiftable && (
            <FlexItem grow>
              <ExclusiveButton
                key="give-as-gift"
                priority="primary"
                onClick={onGiveAsGift}
                biData={giftingBiData}
                variant="compact"
              >
                <GiftAnimated size={IconSize.MEDIUM} disableDefaultColors />
                {t('sidebar.exclusives.buttons.seller.gift.label')}
              </ExclusiveButton>
            </FlexItem>
          )}
        </Flex>
      }
    </React.Fragment>
  );
};
SellerButtons.displayName = 'SellerButtons';

export default SellerButtons;
